import type { RequesterIdentity } from "../entitlements-api";
import { requesterLabel } from "./requester-label";

export interface RequesterTextProps {
  readonly request: {
    readonly requesterId: string;
    readonly requester: RequesterIdentity;
  };
  /**
   * Distinguishes this rendering from every other one on the page.
   *
   * A test id built from the requester alone is NOT unique: one requester can
   * own several queued rows (the reason the lookup deduplicates subs at all),
   * and the same request is named again in the approval confirmation and the
   * denial dialog. `getByTestId` throws on more than one match, which would
   * make the repo's preferred selector unusable against this component.
   */
  readonly testId: string;
}

/**
 * The requester, named (ENR-2246).
 *
 * One component for every site that says who asked, so the queue row, the
 * approval confirmation, the denial dialog and the decided row cannot drift
 * into naming the same person three different ways.
 *
 * An unresolved requester shows its id and is LABELLED unresolved rather than
 * left to look like a name nobody recognises. The title carries the same words
 * for a pointer user, and the visible suffix carries them for everyone else,
 * because an operator who cannot tell "this is an id" from "this is a name"
 * goes and asks in a support channel.
 */
export function RequesterText({ request, testId }: RequesterTextProps): React.JSX.Element {
  const label = requesterLabel(request.requester, request.requesterId);
  if (!label.unresolved) {
    return <span data-testid={testId}>{label.text}</span>;
  }
  return (
    <span data-testid={testId} title="This requester could not be resolved to a name.">
      <span className="font-mono text-[0.95em]">{label.text}</span>{" "}
      <span className="text-muted-foreground">(unresolved)</span>
    </span>
  );
}
